import React, { useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.jsx';
import { DEMO_ACCOUNTS, DEMO_PASSWORD, landingRouteForRole } from './loginRoles.js';
import './login.scss';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const FEATURES = [
  { icon: 'fa-charging-station', title: 'Vendor-Neutral CSMS', text: 'Works with any OCPP 1.6-J or 2.0.1 charger, so a mixed hardware fleet runs on one platform.' },
  { icon: 'fa-clock', title: 'Guaranteed Connector Reservations', text: 'Reserve CCS2 and Type 2 plugs for 15 or 30 minutes with live countdown timers and instant lock.' },
  { icon: 'fa-wallet', title: 'Double-Entry Financial Ledger', text: 'Real-time meter tracking, transparent multi-component tariffs, and PayMongo gateway settlements.' },
];

export default function LoginPage() {
  const { login, isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [alertMsg, setAlertMsg] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (isAuthenticated && !submitting) {
    return <Navigate to={location.state?.from?.pathname || landingRouteForRole(user?.role)} replace />;
  }

  const submit = async (emailValue, passwordValue) => {
    setAlertMsg('');
    setSubmitting(true);
    try {
      const u = await login(emailValue.trim(), passwordValue, rememberMe);
      navigate(location.state?.from?.pathname || landingRouteForRole(u.role), { replace: true });
    } catch (err) {
      setAlertMsg(err.message || 'Email or password is incorrect.');
      setSubmitting(false);
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    const next = {};
    if (!EMAIL_RE.test(email.trim())) next.email = true;
    if (!password) next.password = true;
    setErrors(next);
    if (Object.keys(next).length === 0) submit(email, password);
  };

  const onDemo = (demoEmail) => {
    setEmail(demoEmail);
    setPassword(DEMO_PASSWORD);
    setErrors({});
    submit(demoEmail, DEMO_PASSWORD);
  };

  return (
    <div className="login-shell">
              <aside className="d-none d-lg-flex flex-column justify-content-between text-white login-brand-panel">
                  <div className="d-flex align-items-center login-brand-mark">
                    <div className="rounded-circle d-flex align-items-center justify-content-center me-3 login-brand-icon">
                      <i className="fa fa-bolt fa-lg" aria-hidden="true" />
                    </div>
                    <div>
                      <h3 className="mb-0 fw-bold text-white">ChargeOS</h3>
                      <small className="text-uppercase login-brand-tagline">Powered by Xamun</small>
                    </div>
                  </div>
                <div className="login-brand-body">
                  <h2 className="fw-bold mb-3 text-white login-brand-headline">Next-Generation Open EV Charging Platform</h2>
                  <p className="mb-4 lead login-brand-lead">
                    Seamless, vendor-neutral EV charging management compliant with OCPP 1.6-J and 2.0.1, real-time telemetry, automated billing, and instant connector reservations.
                  </p>
                  <div className="mt-4">
                    {FEATURES.map((f) => (
                      <div className="d-flex align-items-start mb-3" key={f.title}>
                        <div className="me-3 mt-1 login-feature-icon">
                          <i className={`fa ${f.icon} fa-fw`} aria-hidden="true" />
                        </div>
                        <div>
                          <h6 className="mb-1 fw-bold text-white">{f.title}</h6>
                          <small className="login-feature-text">{f.text}</small>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="pt-4 border-top small d-flex justify-content-between align-items-center login-brand-footer">
                  <span className="login-feature-text">&copy; 2026 ChargeOS Inc. All rights reserved.</span>
                  <span className="badge login-ocpp-badge">OCPP 1.6-J / 2.0.1</span>
                </div>
              </aside>

              <section className="login-form-panel">
              <div className="login-form-inner">
                <div className="d-lg-none d-flex align-items-center mb-4">
                  <i className="fa fa-bolt fa-2x me-2 text-primary" aria-hidden="true" />
                  <h4 className="mb-0 fw-bold text-primary">ChargeOS</h4>
                </div>

                <div className="mb-4">
                  <h1 className="h3 fw-bold mb-2 text-primary">Welcome Back</h1>
                  <p className="text-muted">Sign in to your ChargeOS account to manage charging, wallets, and operations.</p>
                </div>

                <div
                  id="login-alert"
                  className={`alert alert-danger${alertMsg ? '' : ' d-none'}`}
                  role="alert"
                  aria-live="polite"
                >
                  <i className="fa fa-circle-exclamation me-2" aria-hidden="true" />
                  <span id="login-alert-msg">{alertMsg}</span>
                </div>

                <form id="login-form" noValidate onSubmit={onSubmit}>
                  <div className="mb-3">
                    <label htmlFor="email" className="form-label small fw-bold">Email Address</label>
                    <div className="input-group has-validation">
                      <span className="input-group-text bg-light border-end-0">
                        <i className="fa fa-envelope text-muted" aria-hidden="true" />
                      </span>
                      <input
                        type="email"
                        className={`form-control border-start-0${errors.email ? ' is-invalid' : ''}`}
                        id="email"
                        name="email"
                        placeholder="name@example.com"
                        autoComplete="username"
                        autoFocus
                        required
                        aria-describedby="email-feedback"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                      />
                      <div id="email-feedback" className="invalid-feedback">Please enter a valid email address.</div>
                    </div>
                  </div>

                  <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center">
                      <label htmlFor="password_hash" className="form-label small fw-bold">Password</label>
                      <Link to="/forgot-password" className="small fw-semibold text-primary">Forgot password?</Link>
                    </div>
                    <div className="input-group has-validation">
                      <span className="input-group-text bg-light border-end-0">
                        <i className="fa fa-lock text-muted" aria-hidden="true" />
                      </span>
                      <input
                        type={showPassword ? 'text' : 'password'}
                        className={`form-control border-start-0 border-end-0${errors.password ? ' is-invalid' : ''}`}
                        id="password_hash"
                        name="password_hash"
                        placeholder="••••••••"
                        autoComplete="current-password"
                        required
                        aria-describedby="password-feedback"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                      />
                      <button
                        className="btn btn-outline-secondary border-start-0"
                        type="button"
                        id="toggle-password"
                        title="Show or hide password"
                        onClick={() => setShowPassword((v) => !v)}
                      >
                        <i className={`fa ${showPassword ? 'fa-eye-slash' : 'fa-eye'} text-muted`} id="password-toggle-icon" aria-hidden="true" />
                        <span className="d-none">Toggle password visibility</span>
                      </button>
                      <div id="password-feedback" className="invalid-feedback">Password is required.</div>
                    </div>
                  </div>

                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <div className="form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="remember-me"
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                      />
                      <label className="form-check-label text-muted small" htmlFor="remember-me">Remember me on this device</label>
                    </div>
                  </div>

                  <button type="submit" id="btn-login-submit" className="btn btn-primary w-100 fw-bold py-2 mb-3" disabled={submitting}>
                    {submitting && <span id="btn-submit-spinner" className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />}
                    <span id="btn-submit-text">{submitting ? 'Signing In...' : 'Sign In'}</span>
                  </button>

                  <div className="text-center mt-3 mb-4">
                    <span className="text-muted small">Don&apos;t have an account yet? </span>
                    <Link to="/register" className="fw-bold small text-primary">Create an account</Link>
                  </div>
                </form>

                {/* One-click demo sign-in exists only where a demo password is built in (local development). */}
                {DEMO_PASSWORD && (
                <div className="demo-accounts-card">
                  <div className="login-demo-divider"><span><i className="fa fa-user-group me-1" aria-hidden="true" />Demo accounts — dev only</span></div>
                  <div className="login-demo-list">
                    {DEMO_ACCOUNTS.map((d) => (
                      <button
                        key={d.email}
                        type="button"
                        className="demo-btn"
                        data-email={d.email}
                        title={d.title}
                        disabled={submitting}
                        onClick={() => onDemo(d.email)}
                      >
                        <i className={`fa ${d.icon} fa-fw me-2`} aria-hidden="true" /><strong>{d.label}</strong><span className="demo-btn-note">— {d.title.replace(/^Lands on /, '')}</span>
                      </button>
                    ))}
                  </div>
                </div>
                )}
              </div>
              </section>
    </div>
  );
}
